import axios from 'axios';
import { queueRequest } from './backgroundSync';
import { promptUpgrade } from '../lib/upgradePrompt';
import { blockedFeatureFor, planBlockedError } from '../lib/planGate';

const BACKEND_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL);

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  // ARM Auth-Hardening (iter316, 2026-07) — Send cookies with every
  // request so the HttpOnly session cookie set at login is honoured
  // by the backend. Bearer header stays for backward compat.
  withCredentials: true,
});

// Track if we're currently refreshing the token
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Helper to get the active storage based on user preference
const getAuthStorage = () => {
  const storageType = localStorage.getItem('authStorage');
  return storageType === 'session' ? sessionStorage : localStorage;
};

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    const storage = getAuthStorage();
    const token = storage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Wave 24u.3 — Parent view-mode header. Written by ViewModeContext.
    // Backend only honours `parent` for admin/coach/staff who actually
    // have children in the current tenant; otherwise it's ignored.
    try {
      const vm = localStorage.getItem('viewMode');
      if (vm && vm !== 'default') {
        config.headers['X-View-Mode'] = vm;
      }
    } catch { /* localStorage unavailable — skip */ }
    // Areas outside the academy's plan are refused locally instead of a 402 round-trip.
    const blocked = await blockedFeatureFor(config.url, Boolean(token));
    if (blocked) return Promise.reject(planBlockedError(blocked, config));
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.isPlanBlocked) return Promise.reject(error);
    const originalRequest = error.config;
    
    // Don't redirect on 401 for auth mutation endpoints (login, register, refresh, etc.)
    // /auth/me should still trigger token refresh
    const isAuthMutationEndpoint = originalRequest?.url?.includes('/auth/login') || 
      originalRequest?.url?.includes('/auth/register') ||
      originalRequest?.url?.includes('/auth/refresh') ||
      originalRequest?.url?.includes('/auth/logout') ||
      originalRequest?.url?.includes('/auth/reset-password') ||
      originalRequest?.url?.includes('/auth/forgot-password');
    const isPlayerEndpoint = originalRequest?.url?.includes('/player/');
    const isRefreshEndpoint = originalRequest?.url?.includes('/auth/refresh');
    // iter318p — /api/auth/me is a bootstrap call that legitimately
    // returns 401 for anonymous visitors on the marketing site. If we
    // redirected them to `/` here, they'd bounce back to the marketing
    // shell which refetches /me → infinite reload loop on www.vyomos.io.
    const isMeEndpoint = originalRequest?.url?.includes('/auth/me');
    
    // If 401 and not an auth mutation endpoint, try to refresh the token
    // iter318p — Also skip /auth/me: it's a bootstrap check that legitimately
    // 401s for anonymous marketing-site visitors. Attempting refresh/redirect
    // for it would loop.
    if (error.response?.status === 401 && !isAuthMutationEndpoint && !isPlayerEndpoint && !isMeEndpoint && !originalRequest._retry) {
      const storage = getAuthStorage();
      const refreshToken = storage.getItem('refreshToken');
      
      // If we have a refresh token, try to use it
      if (refreshToken && !isRefreshEndpoint) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }
        
        originalRequest._retry = true;
        isRefreshing = true;
        
        try {
          const response = await axios.post(`${BACKEND_URL}/api/auth/refresh`, {
            refresh_token: refreshToken
          });
          
          const { access_token, refresh_token: new_refresh_token } = response.data;
          storage.setItem('token', access_token);
          // iter307be / SEC-4 — Backend rotates RT on every /refresh.
          // Persist the new RT so the next refresh uses the current,
          // un-revoked one. If the response omits it (older backend),
          // keep the existing RT for backwards compatibility.
          if (new_refresh_token) {
            storage.setItem('refreshToken', new_refresh_token);
          }
          
          processQueue(null, access_token);
          
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          
          // Refresh failed, clear tokens and redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('authStorage');
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('refreshToken');
          window.location.href = '/';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
      
      // No refresh token — clear and (conditionally) redirect.
      // iter318p — Only bounce to `/` if we had a stored token that
      // just went stale (real session-expiry). Anonymous visitors on
      // the marketing site legitimately hit 401 on /auth/me — sending
      // them to `/` would reload the same page and start an infinite
      // loop. Same for /auth/me itself: never redirect on it.
      const hadStoredToken =
        !!(storage.getItem('token') || storage.getItem('refreshToken'));
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('authStorage');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refreshToken');
      if (!isMeEndpoint && hadStoredToken) {
        window.location.href = '/';
      }
    }
    
    // Queue failed mutation requests when offline (network errors only, not server errors)
    if (!error.response && originalRequest && ['post', 'put', 'patch', 'delete'].includes(originalRequest.method)) {
      const isQueueable = !isAuthMutationEndpoint && !originalRequest.url?.includes('/auth/');
      if (isQueueable && !navigator.onLine) {
        const fullUrl = originalRequest.baseURL
          ? `${originalRequest.baseURL}${originalRequest.url}`
          : originalRequest.url;
        const queued = await queueRequest(
          fullUrl,
          originalRequest.method.toUpperCase(),
          originalRequest.headers || {},
          originalRequest.data
        );
        if (queued) {
          const queuedError = new Error('Request queued for background sync');
          queuedError.queued = true;
          queuedError.offline = true;
          return Promise.reject(queuedError);
        }
      }
    }

    // One-shot retry for transient GET failures (cold start, 502/503/504,
    // or "offline"-flagged network blips). Keeps intermittent errors out of
    // the admin console without masking real server failures.
    const status = error.response?.status;
    const isTransientStatus = status === 502 || status === 503 || status === 504;
    const isNetworkBlip = !error.response && navigator.onLine && !error.queued;
    const isGetRequest = originalRequest?.method === 'get';
    if (
      isGetRequest &&
      !originalRequest._transientRetry &&
      (isTransientStatus || isNetworkBlip)
    ) {
      originalRequest._transientRetry = true;
      await new Promise((r) => setTimeout(r, 500));
      return api(originalRequest);
    }

    // ARM iter316 (2026-07) — Race-refresh toast. When a lifecycle
    // handler (approve / pause / cancel / resume) returns 409 with an
    // "Another admin ..." detail, surface a friendly toast instead of
    // the raw error and dispatch a global refresh event so the calling
    // page can re-fetch its list.
    if (
      error.response?.status === 409 &&
      typeof error.response.data?.detail === "string" &&
      /another admin|refresh|status changed underneath/i.test(error.response.data.detail)
    ) {
      try {
        // Toast (imported lazily so we don't pull the shadcn tree here).
        const { toast } = await import("sonner");
        toast.info("Another admin just changed this — refreshing…", {
          description: error.response.data.detail,
          duration: 3500,
        });
      } catch {
        // sonner not available — fall through to console.
        console.info("[race-refresh] 409:", error.response.data.detail);
      }
      // Broadcast a global event so lists can auto-refresh.
      try {
        window.dispatchEvent(new CustomEvent("arm:race-refresh", {
          detail: {
            method: originalRequest?.method,
            url: originalRequest?.url,
          },
        }));
      } catch {
        // window not available (unlikely in browser context) — ignore.
      }
    }

    return Promise.reject(error);
  }
);

export default api;
